import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ContextMenuButton, NameEmailAvatar } from '../../../../ui';
import { OrganizationUser } from '../../../../utils/types/admin_types';

// Tooltip component for displaying truncated text with an accessible MUI tooltip
const TooltipWrapper: React.FC<{ text?: string | null }> = ({ text }) => {
  const displayText = text || '-';
  return (
    <Tooltip title={displayText} arrow placement='top'>
      <Typography
        sx={{
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.8125rem',
          color: 'text.secondary',
          cursor: 'default',
        }}
      >
        {displayText}
      </Typography>
    </Tooltip>
  );
};

const columns = [
  { title: 'Name' },
  { title: 'Groups' },
  { title: 'Last Active' },
  { title: 'Actions' },
];

interface GetColumnsArgs {
  handleOpenMenu: (
    e: React.MouseEvent<HTMLButtonElement>,
    row: OrganizationUser,
  ) => void;
  handleCloseMenu: () => void;
  anchorEl: HTMLElement | null;
}

export const getColumns = ({
  handleOpenMenu,
  handleCloseMenu,
  anchorEl,
}: GetColumnsArgs) => {
  return columns.map((col) => {
    if (col.title === 'Name') {
      return {
        key: 'Name',
        label: 'User',
        width: '250px',
        align: 'left' as const,
        render: (row: OrganizationUser) => (
          <NameEmailAvatar
            email={row.user?.email}
            name={`${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim()}
            avatar={row.user?.profilePic}
          />
        ),
        disableSort: true,
      };
    }

    if (col.title === 'Groups') {
      return {
        key: 'Groups',
        label: 'Groups',
        width: '250px',
        align: 'left' as const,
        render: (row: OrganizationUser) => (
          <TooltipWrapper
            text={row.groups?.map((group) => group.groupName).join(', ')}
          />
        ),
        disableSort: true,
      };
    }

    if (col.title === 'Last Active') {
      return {
        key: 'Last Active',
        label: 'Last Active',
        width: '250px',
        align: 'left' as const,
        render: (row: OrganizationUser) => (
          <TooltipWrapper
            text={
              row.lastActive
                ? new Date(row.lastActive * 1000).toLocaleString()
                : '-'
            }
          />
        ),
        disableSort: true,
      };
    }

    if (col.title === 'Actions') {
      return {
        key: 'Actions',
        label: '',
        width: '100px',
        align: 'right' as const,
        render: (row: OrganizationUser) => (
          <ContextMenuButton
            row={row}
            onOpenMenu={(e, r) => handleOpenMenu(e, r as OrganizationUser)}
            onCloseMenu={handleCloseMenu}
            anchorEl={anchorEl}
          />
        ),
        disableSort: true,
      };
    }

    return {
      key: col.title,
      label: col.title,
      width: '250px',
      align: 'left' as const,
      render: (row: Record<string, unknown>) => (
        <TooltipWrapper text={String(row[col.title] ?? '')} />
      ),
      disableSort: true,
    };
  });
};
